import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const KEYS = {
  language: "addy.pref.language",
  theme: "addy.pref.theme",
  notifyAppointments: "addy.pref.notifyAppointments",
  notifyPayments: "addy.pref.notifyPayments",
};

function read(k: string, dflt: string): string {
  return localStorage.getItem(k) ?? dflt;
}

/** T80 — Tuỳ chọn cá nhân. Lưu trữ thuần FE qua localStorage; backend đồng bộ sẽ thêm sau. */
export function MyPreferencesPage() {
  const [language, setLanguage] = useState(read(KEYS.language, "vi"));
  const [theme, setTheme] = useState(read(KEYS.theme, "light"));
  const [notifyAppt, setNotifyAppt] = useState(read(KEYS.notifyAppointments, "true") === "true");
  const [notifyPay, setNotifyPay] = useState(read(KEYS.notifyPayments, "true") === "true");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(t);
  }, [saved]);

  function save(e: React.FormEvent) {
    e.preventDefault();
    localStorage.setItem(KEYS.language, language);
    localStorage.setItem(KEYS.theme, theme);
    localStorage.setItem(KEYS.notifyAppointments, String(notifyAppt));
    localStorage.setItem(KEYS.notifyPayments, String(notifyPay));
    setSaved(true);
  }

  return (
    <div>
      <PageHeader
        title="Tuỳ chọn cá nhân"
        description="Ngôn ngữ, giao diện, thông báo — lưu trên trình duyệt này (đồng bộ tài khoản sẽ thêm sau)."
      />

      <form onSubmit={save} className="mt-4 max-w-[540px]">
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-1.5">
              <Label htmlFor="pref-language">Ngôn ngữ</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="pref-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vi">Tiếng Việt</SelectItem>
                  <SelectItem value="en">English (xem trước)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pref-theme">Giao diện</Label>
              <Select value={theme} onValueChange={setTheme}>
                <SelectTrigger id="pref-theme">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Sáng</SelectItem>
                  <SelectItem value="dark">Tối (xem trước)</SelectItem>
                  <SelectItem value="system">Theo hệ thống</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="pref-notify-appt"
                checked={notifyAppt}
                onCheckedChange={(v) => setNotifyAppt(v === true)}
              />
              <Label htmlFor="pref-notify-appt" className="font-normal">
                Nhận thông báo lịch hẹn mới / đến giờ
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="pref-notify-pay"
                checked={notifyPay}
                onCheckedChange={(v) => setNotifyPay(v === true)}
              />
              <Label htmlFor="pref-notify-pay" className="font-normal">
                Nhận thông báo khi có thanh toán
              </Label>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit">Lưu tuỳ chọn</Button>
              {saved && (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600">
                  <Check className="h-4 w-4" /> Đã lưu
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
